import { faClose } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Tooltip from '@mui/material/Tooltip';
import { useCallback, useEffect, useRef, useState } from 'react';

import useStyles from './Panel.styles';

const Panel = ({
  action,
  children,
  onClose,
  title
}: {
  action?: React.ReactNode;
  children: React.ReactNode;
  onClose?: () => void;
  title: React.ReactNode | string;
}) => {
  const { classes } = useStyles();
  const titleRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const checkOverflow = useCallback(() => {
    if (titleRef.current) {
      const isOverflowing =
        titleRef.current.scrollWidth > titleRef.current.clientWidth;
      setShowTooltip(isOverflowing);
    }
  }, [titleRef, panelRef]);

  // Checks to see if title is overflowing when panel is resized
  useEffect(() => {
    if (!panelRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });
    resizeObserver.observe(panelRef.current);
    return () => resizeObserver.disconnect();
  }, [panelRef, title]);

  return (
    <Card ref={panelRef} className={classes.panel}>
      <CardHeader
        className={classes.panelHeader}
        title={
          <Tooltip title={showTooltip ? title : ''}>
            <span className={classes.panelHeaderTitleText} ref={titleRef}>
              {title}
            </span>
          </Tooltip>
        }
        slotProps={{ title: { className: classes.panelHeaderTitleText } }}
        {...((action || onClose) && {
          action: (
            <>
              {action}
              {onClose && (
                <Button
                  className={classes.panelCloseButton}
                  size="medium"
                  variant="outlined"
                  onClick={onClose}
                >
                  <FontAwesomeIcon icon={faClose} />
                </Button>
              )}
            </>
          )
        })}
      />
      <CardContent className={classes.panelContent}>{children}</CardContent>
    </Card>
  );
};

export default Panel;
